/**
 * ICD-10-CM reference service: search and single-code check against the loaded CMS release.
 * Used by the diagnosis list, referrals and the assessment ICD-10 fields (through Icd10Picker).
 */

import { apiClient } from '../api-client';
import type { ApiResponse, QueryParams } from '../../dtos/common/api.dto';
import type { Icd10CheckResult, Icd10SearchResponse } from '../../dtos/reference/icd10.dto';

/** A query is a code (or the start of one) when a digit is in position 2 or 3: G40, QA0, U07. */
export const looksLikeIcd10Code = (q: string): boolean => /^[A-Z](?:[0-9][0-9A-Z]|[A-Z][0-9])(\.?[0-9A-Z]{0,4})?$/i.test(q.trim());

export class Icd10Service {
  /**
   * Search today's release. Code-shaped text matches by code prefix (header codes included so
   * the picker can say "choose a more specific code"); other text matches description words.
   */
  static async search(q: string, options: { limit?: number } = {}): Promise<Icd10SearchResponse> {
    const params: QueryParams = { q: q.trim(), limit: options.limit ?? 20 };
    if (looksLikeIcd10Code(q)) params.includeHeaders = 'true';
    const response: ApiResponse<Icd10SearchResponse> = await apiClient.get('/reference/icd10/search', params);
    if (response.success && response.data) return { releaseId: response.data.releaseId, codes: response.data.codes || [] };
    throw new Error(response.error?.message || 'ICD-10 search failed');
  }

  /** Whether a code is a billable code in the release in effect on dos (default today). */
  static async check(code: string, dos?: string): Promise<Icd10CheckResult> {
    const response: ApiResponse<Icd10CheckResult> = await apiClient.get(
      `/reference/icd10/${encodeURIComponent(code.trim())}`,
      dos ? { dos } : undefined
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error?.message || 'ICD-10 check failed');
  }
}
